import {defineComponent, PropType} from 'vue';
import * as scrollTo from 'scroll-to';
import encodeFormData from '../../js/encodeFormData/encodeFormData';
import httpRequest, {Method} from '../../js/httpRequest/httpRequest';
import pigeon from '../../js/pigeon/pigeon.js';
import jsGlobalConfig from '../../../globals/jsGlobalConfig';
import {getMaxDate, getMinDate} from '../../js/validation/validationHelper';
//import type CaptchaInput from '../../../components/captchaInput/captchaInput.vue';
import validationMixin from '../validationMixin/validationMixin';

type FieldError = {
  field: string,
  msg: string
}

type FormValidationResult = {
  fieldErrors?: FieldError[],
  responseObject?: any,
  redirectOnRestriction?: string,
}

interface FormResponse {
  data: FormValidationResult | {formResult: FormValidationResult};
  status: number;
}

export default defineComponent({
  name: 'FormMixin2',

  extends: validationMixin,

  props: {
    successUrl: {
      type: String as PropType<string>,
      default: '',
    },
    action: {
      type: String as PropType<string>,
      default: '',
    },
    id: {
      type: String as PropType<string>,
      default: '',
    },
    method: {
      type: String as PropType<Method>,
      default: 'POST'
    },
    eventOnSuccess: {
      type: Object as PropType<{ event: string, data: any }>,
      default: null
    },
  },

  data() {
    return {
      innerSuccessUrl: this.successUrl,
      innerAction: this.action,
      innerId: this.id,
      anonAttribute: jsGlobalConfig().getInputAnonymization(),
      getMinDate: getMinDate,
      getMaxDate: getMaxDate,

      captchaInput: null, //as InstanceType<typeof CaptchaInput> | null,
      captchaToken: '',
    }
  },

  mounted() {
    this.innerSuccessUrl = this.successUrl;
    this.innerAction = this.action;
    this.innerId = this.id;
    this.captchaInput = this.$refs.captchaInput;
  },

  methods: {
    // submits the serialized form with ajax
    submitFormAjax(form: HTMLFormElement, preserveEmptyFields = true) {
      return this.submit(
        encodeFormData(this.serializeForm(form), preserveEmptyFields)
      );
    },

    submitFormModelAjax(model: Record<string, any>) {
      const formData = {
        ...model,
        ...jsGlobalConfig().getCsrfTokenData()
      }
      return this.submit(encodeFormData(formData));
    },

    serializeForm(form) {
      const data: Record<string, any> = {};

      // Loop through each field in the form
      for (let i = 0; i < form.elements.length; i++) {

        const field = form.elements[i];

        // Don't serialize fields without a name, submits, buttons, file and reset inputs, and disabled fields
        if (!field.name || field.disabled || field.type === 'file' || field.type === 'reset' || field.type === 'submit' || field.type === 'button') continue;

        // Don't serialize empty captchas
        if ((field.name === 'h-captcha-response' && !field.value) || (field.name === 'captchaToken' && !field.value)) {
          continue;
        }

        // If a multi-select, get all selections
        if (field.type === 'select-multiple') {
          for (let n = 0; n < field.options.length; n++) {
            if (!field.options[n].selected) continue;
            data[field.name] = field.options[n].value;
          }
        }

        // Convert field data to a query string
        else if ((field.type !== 'checkbox' && field.type !== 'radio') || field.checked) {
          data[field.name] = field.value;
        }
      }

      return data;
    },

    serializeData(data: Record<string, unknown>, preserveEmptyFields = true) {
      return encodeFormData(data, preserveEmptyFields);
    },

    submit(data: string): Promise<any> {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        this.clearServerSideErrors();
        const validationSuccess: boolean = await this.v$.$validate();
        if (validationSuccess) {
          if (this.captchaInput && !data.match(/captchaToken=[a-zA-Z\d\-.]+/)) {
            const token = await this.captchaInput.execute();
            if (token.response !== 'error') {
              const tokenValue = token.response !== 'ok' ? token.response : token.key;
              const captchaData = encodeFormData({captchaToken: tokenValue});
              data += '&' + captchaData;
            } else {
              reject({captchaError: true});
              return;
            }
          }
          httpRequest(this.innerAction, {
            method: this.method,
            data: data
          }).then((response: FormResponse) => {
            let fieldErrors = [];
            if (response.data) {
              fieldErrors = 'formResult' in response.data ? response.data.formResult.fieldErrors : response.data.fieldErrors;
            }
            if (fieldErrors && fieldErrors.length) {
              fieldErrors.forEach(fieldError => {
                this.createServerSideError(fieldError.field, fieldError.msg);
              });
              this.$nextTick(() => {
                this.scrollToFirstError(this);
                this.captchaInput?.reset();
              });
              reject({validationResult: false, serverSideValidationResult: false});
              return;
            } else if (response.data && 'redirectOnRestriction' in response.data) {
              window.location.href = response.data.redirectOnRestriction;
            } else {
              if (this.eventOnSuccess) {
                pigeon.publish(this.eventOnSuccess.event, this.eventOnSuccess.data);
              }
              if (this.innerSuccessUrl) {
                window.location.href = this.innerSuccessUrl;
              }
              if (this.innerId) {
                let successObj = {id: this.innerId};
                if ('responseObject' in response.data) {
                  successObj['responseObj'] = response.data.responseObject;
                }

                pigeon.publish('formsubmit:success', successObj);
              }
            }
            resolve(response);

          }).catch(error => {
            if (!error.alreadyHandled) {
              const data = {
                message: this.$t('global_app_error.form_error'),
                error: error
              };
              pigeon.publish('globalAppMessage:show', data);
            }
            reject(error);
          });
        } else {
          // !validationSuccess
          this.$nextTick(() => {
            this.scrollToFirstError(this);
          });
          reject({validationResult: false});
        }
      });
    },

    async submitForm(form: HTMLFormElement) {
      const validationSuccess = await this.v$.$validate();
      if (validationSuccess) {
        if (this.captchaInput && this.captchaInput.invisible) {
          const token = await this.captchaInput.execute();
          if (token.response === 'error') {
            return false;
          }
        }

        if (this.eventOnSuccess) {
          pigeon.publish(this.eventOnSuccess.event, this.eventOnSuccess.data);
        }
        form.submit();
      } else {
        this.$nextTick(() => {
          this.scrollToFirstError(this);
        });
      }

      return validationSuccess;
    },

    scrollToFirstError(vm) {
      let rootElement: HTMLElement = vm.$el;
      if (rootElement.childNodes.length === 0) {
        rootElement = document.body;
      }
      if (vm.id) {
        pigeon.publish('formsubmit:error', vm.id);
      }

      const firstErrorMessage = rootElement.querySelector('.input-error');
      if (!firstErrorMessage) {
        // no error in scope
        return;
      }

      const bodyRect = document.body.getBoundingClientRect();
      const elemRect = firstErrorMessage.parentElement.getBoundingClientRect();
      const scrollToPosition = elemRect.top - bodyRect.top - 100;
      scrollTo(0, scrollToPosition, {duration: 500});

      const firstErrorInput = rootElement.querySelector('.input__wrapper--error');
      if (firstErrorInput) {
        const firstErrorInputField: HTMLInputElement = firstErrorInput.querySelector('.input__field');
        const validSelector = ':not(.input__field--date):not([name="userDataViewmodel.email"])';
        if (firstErrorInputField && firstErrorInputField.matches(validSelector)) {
          firstErrorInputField.focus();
        }
      }
    },

    showMandatory(): boolean {
      const text = this.$t('form.mandatory_notice');
      return text && text.length > 0;
    },

    captchaValidationRule(): Record<string, unknown> {
      const captcha: any = this.captchaInput;
      return captcha && !captcha.invisible ? this.getValidationRules('none', true, 'captchaToken') : {};
    },
  },
})
